import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

type NavigationKind = 'push' | 'replace' | 'back' | 'forward';

/**
 * Knows whether the user reached the current page by navigating inside the app, so a "back" button can
 * return to where they came from (query params included) and otherwise fall back to the page's logical
 * parent. A deep link or a reload has no in-app history: a plain browser back would leave the app.
 *
 * Instantiated at startup (app.module) so the very first navigation is seen.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  /** In-app entries behind the current one. */
  private depth = 0;
  /** Router navigation id of the history entry that is showing; popstate state carries the id of its entry. */
  private entryId = 0;
  private pending: NavigationKind = 'replace';
  private pendingEntryId = 0;

  constructor(private router: Router, private location: Location) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.onStart(event);
      } else if (event instanceof NavigationEnd) {
        this.onEnd(event);
      }
    });
  }

  /** True when the previous history entry is a page of this app. */
  get canGoBack(): boolean {
    return this.depth > 0;
  }

  /**
   * Back to the previous in-app page (its query params included), else to the fallback parent.
   * `fallback` is a router command array or a URL.
   */
  back(fallback: any[] | string): Promise<boolean> {
    if (this.canGoBack) {
      this.location.back();
      return Promise.resolve(true);
    }
    return typeof fallback === 'string' ? this.router.navigateByUrl(fallback) : this.router.navigate(fallback);
  }

  private onStart(event: NavigationStart): void {
    if (event.navigationTrigger === 'popstate') {
      const restored = event.restoredState?.navigationId;
      this.pendingEntryId = restored ?? this.entryId;
      // Without a restored id the direction is unknown; assume back, which errs towards the safe fallback.
      this.pending = restored == null || restored < this.entryId ? 'back' : 'forward';
    } else if (this.entryId === 0 || this.router.currentNavigation()?.extras.replaceUrl) {
      // The first navigation of the page load has nothing behind it; a replaced URL adds no entry.
      this.pending = 'replace';
    } else {
      this.pending = 'push';
    }
  }

  private onEnd(event: NavigationEnd): void {
    switch (this.pending) {
      case 'push':
      case 'forward':
        this.depth++;
        break;
      case 'back':
        this.depth = Math.max(0, this.depth - 1);
        break;
    }
    // A push or a replace stamps the entry with this navigation's id; a popstate lands on an older entry.
    this.entryId = this.pending === 'back' || this.pending === 'forward' ? this.pendingEntryId : event.id;
  }
}
